import { Card, CardContent } from "@/components/ui/card";
import ContactForm from "@/components/forms/ContactForm";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const Contact = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "+1-871-7871311",
      link: "tel:+18717871311",
      description: "Mon-Sat, 8am-6pm EST",
    },
    {
      icon: Mail,
      title: "Email",
      content: "support@cartnexa.store",
      link: "mailto:support@cartnexa.store",
      description: "We respond within 24 hours",
    },
    {
      icon: MapPin,
      title: "Address",
      content: "783 Meadowbrook Lane",
      description: "Atlanta, GA 30303",
      link: null,
    },
    {
      icon: Clock,
      title: "Business Hours",
      content: "Mon - Sat: 8am - 6pm",
      description: "Sunday: Closed",
      link: null,
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Contact Us</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mt-4">
              Get in Touch
            </h1>
            <p className="text-lg text-slate-600 mt-6">
              Have questions or ready to book? We're here to help. Reach out and we'll respond within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="bg-slate-50 border-0 rounded-2xl" data-testid={`contact-info-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <info.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-secondary mb-2">{info.title}</h3>
                  {info.link ? (
                    <a
                      href={info.link}
                      className="text-primary font-medium hover:text-primary/80 transition-colors"
                      data-testid={`contact-${info.title.toLowerCase()}-link`}
                    >
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-slate-700 font-medium">{info.content}</p>
                  )}
                  <p className="text-slate-500 text-sm mt-1">{info.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Form & Map Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-6">
                Send Us a Message
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible. For immediate assistance, please call us.
              </p>
              <Card className="bg-slate-50 border-0 rounded-2xl">
                <CardContent className="p-8">
                  <ContactForm />
                </CardContent>
              </Card>
            </div>

            {/* Map & Info */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-6">
                Our Location
              </h2>
              <p className="text-slate-600 mb-8">
                Serving Atlanta and the greater metro area. Our main office is conveniently located in downtown Atlanta.
              </p>
              
              {/* Map Placeholder */}
              <div className="rounded-2xl overflow-hidden shadow-lg mb-8" data-testid="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d106161.75971891584!2d-84.47383565!3d33.76919545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f5045d6993098d%3A0x66fede2f990b630b!2sAtlanta%2C%20GA!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="CartNexa Location"
                ></iframe>
              </div>

              {/* Additional Info */}
              <Card className="bg-primary text-white rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-bold mb-4">Why Contact Us?</h3>
                  <ul className="space-y-3 text-white/90">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm flex-shrink-0">1</span>
                      <span>Get a free, no-obligation quote</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm flex-shrink-0">2</span>
                      <span>Ask questions about our services</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm flex-shrink-0">3</span>
                      <span>Schedule your first cleaning</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm flex-shrink-0">4</span>
                      <span>Provide feedback on our service</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Click-to-Call CTA */}
      <section className="py-16 bg-secondary" data-testid="contact-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Prefer to Talk?
          </h2>
          <p className="text-slate-300 mb-6">
            Our friendly team is ready to help you with any questions.
          </p>
          <a
            href="tel:+18717871311"
            className="inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white font-bold text-xl px-8 py-4 rounded-full transition-all btn-accent-hover"
            data-testid="contact-call-cta"
          >
            <Phone className="w-6 h-6" />
            +1-871-7871311
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
